/**
 * Which pages this person opens most, for the quick-access bar at the bottom of a phone
 * (owner, 2026-09-27: "bottom add menu like quick access icons based on recent clicks?" -
 * "it is based on the user?": yes).
 *
 * PER USER, ON THIS DEVICE. The scores are kept in localStorage under the signed-in user's id,
 * so two people who share a handset each get their own bar, and nobody's habits leave the
 * phone. Carrying them to a second device would take a table on the server; nothing here is
 * worth a migration yet.
 *
 * A visit adds one; everything already there fades by half every fortnight first. So the bar
 * follows what somebody does THIS month, not what they did in training.
 */
const HALF_LIFE_DAYS = 14;

interface Score { score: number; at: number }

function storageKey(userId: string): string {
  return `vanam.nav.recent.${userId}`;
}

function read(userId: string): Record<string, Score> {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    return raw ? (JSON.parse(raw) as Record<string, Score>) : {};
  } catch {
    return {};
  }
}

function faded(entry: Score, now: number): number {
  const days = Math.max(0, now - entry.at) / 86_400_000;
  return entry.score * 0.5 ** (days / HALF_LIFE_DAYS);
}

/** Count one opening of `key` for this person. */
export function recordVisit(userId: string, key: string): void {
  const now = Date.now();
  const scores = read(userId);
  const current = scores[key];
  scores[key] = { score: (current ? faded(current, now) : 0) + 1, at: now };
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(scores));
  } catch {
    // A convenience only: a full or blocked store loses nothing that matters.
  }
}

/** The `limit` most-opened keys among `allowed`, best first. Fewer when there is no history yet. */
export function mostVisited(userId: string, allowed: string[], limit: number): string[] {
  const now = Date.now();
  const scores = read(userId);
  return allowed
    .filter((key) => scores[key])
    .map((key) => ({ key, score: faded(scores[key]!, now) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.key);
}
